// lib
import React from 'react';
import { createRoot } from 'react-dom/client';
import DayJs from 'dayjs';
import RelativeTime from 'dayjs/plugin/relativeTime';
import { ApolloProvider } from '@apollo/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6';
import { HelmetProvider } from 'react-helmet-async';
import { PersistGate } from 'redux-persist/integration/react';
import * as Sentry from '@sentry/react';

import smoothscroll from 'smoothscroll-polyfill';

// Styles
import '@fontsource-variable/nunito-sans';
import './assets/css/normalizer.css';
import './tailwind-built.css';

// Utils
// import Sentry from './utils/Sentry';
import './utils/Helpers';

// Workers
// import * as serviceWorker from './serviceWorker';

// Store
import Client from './apollo';
import Store, { persist } from './store';

// Services
import { ErrorCategory, handleError } from '@services/errorService';
import { registerGlobalNotify } from '@services/errorService';

// Actions
import { ShowToast } from '@actions/toast';

// Routes
import App from './components/App';
import {
  ThemeProvider,
  applyThemeClass,
  readStoredTheme,
} from './components/ThemeProvider';

smoothscroll.polyfill();

// Apply the persisted theme synchronously, before first paint, to avoid a
// light→dark flash on reload.
applyThemeClass(readStoredTheme());

// Register global toast notifier for errorService
registerGlobalNotify((message) => {
  Store.dispatch(ShowToast({ title: 'Error', message }));
});

DayJs.extend(RelativeTime);
// Sentry();

Sentry.init({
  dsn: 'https://8f462bcdb36455ea98e728e752507aa7@o1130536.ingest.us.sentry.io/4507329353613312',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [/^https:\/\/mrbl\.bio\/api/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

// Debug logging only in development
const isDev = process.env.NODE_ENV === 'development';

if (isDev) {
  // eslint-disable-next-line no-console
  console.log('DEBUG | index.js | Initializing application...');
}

// Check if version changed and clear cache if needed
if (!isDev) {
  import('./utils/version')
    .then(({ checkVersionAndClearCache }) => {
      checkVersionAndClearCache().then((cacheCleared) => {
        if (cacheCleared) {
          window.location.reload();
        }
      });
    })
    .catch((error) => {
      handleError(error, { category: ErrorCategory.API, silent: true });
    });
}

// React 18 createRoot API
const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <HelmetProvider>
    <ApolloProvider client={Client()}>
      <Provider store={Store}>
        <PersistGate loading={null} persistor={persist}>
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <NuqsAdapter>
              <ThemeProvider>
                <App />
              </ThemeProvider>
            </NuqsAdapter>
          </BrowserRouter>
        </PersistGate>
      </Provider>
    </ApolloProvider>
  </HelmetProvider>,
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
// serviceWorker.unregister();
